/**
 * Membership Page
 * Select membership plans with Quarterly and Annual options
 * iOS-first design with consistent styling
 *
 * @author Varun Pandey
 */

import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBigRightDash,
  BadgeCheck,
  Check,
  ExternalLink,
  GitCompare,
  Image,
  Percent,
  Rocket,
  Star,
  Video,
  Wallet,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import confetti from 'canvas-confetti';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import { useArtistDetailsStore } from '../../stores/artistDetailsStore';
import { useAuthStore } from '@/stores/authStore';
import apiClient from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import {
  fetchMembershipWebsiteLinkByPhone,
  membershipCheckoutUsesIap,
  membershipEmiAvailable,
  purchaseMembership,
  restoreMembershipPurchases,
} from '@/services/membershipPaymentService';
import { isRevenueCatEnabled } from '@/services/revenueCatService';
import { Loader } from '@/components/ui';
import { Checkbox } from '@/components/ui/Checkbox';
import { IosSubscriptionLegalDisclosure } from '@/components/membership/IosSubscriptionLegalDisclosure';
import { MembershipPlanPrice } from '@/components/membership/MembershipPlanPrice';
import {
  IOS_SUBSCRIPTION_PRICES_KEY,
  useIosSubscriptionPrices,
} from '@/hooks/useIosSubscriptionPrices';
import { useQueryClient } from '@tanstack/react-query';
import { useRevenueCatAppUserId } from '@/hooks/useRevenueCatAppUserId';
import { resolveRevenueCatAppUserId } from '@/utils/revenueCatAppUserId';
import { hasValidAuthCookies } from '@/utils/cookies';
import {
  getMembershipPlansForCategory,
  getPlanDisplayPrice,
  getPlanEmiAmount,
  resolveMembershipPlanKey,
  type Plan,
} from '@/data/membershipPlansData';
import { isNativeIOSApp } from '@/utils/platform';

type BillingPeriod = 'quarterly' | 'annually';

type EliteEligibilityType = 'quarterly' | 'annually';

type EliteEligibilityStatus = 'pending' | 'accepted' | string;

type EliteEligibility = {
  id: number;
  artist: number;
  status: EliteEligibilityStatus;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  expired_time: string | null;
  accepted_at: string | null;
  type: EliteEligibilityType;
};

type MembershipRedirectBannerSlide = {
  benefit: string;
  Icon: LucideIcon;
  containerClass: string;
  glowClass: string;
  iconWrapClass: string;
  textClass: string;
  dotActiveClass: string;
};

const MEMBERSHIP_REDIRECT_BANNER_SLIDES: MembershipRedirectBannerSlide[] = [
  {
    benefit: 'Get upto 8 Express Gigs in a month',
    Icon: Zap,
    containerClass:
      'border-pink-500/35 bg-gradient-to-br from-[#3D1A2E] via-[#26141F] to-[#141018]',
    glowClass: 'bg-pink-500/25',
    iconWrapClass: 'bg-pink-500/20 text-pink-300 ring-pink-400/40',
    textClass: 'text-pink-50',
    dotActiveClass: 'bg-[#F55C8D]',
  },
  {
    benefit: 'Reduced Platform Fee upto 10%',
    Icon: Percent,
    containerClass:
      'border-emerald-500/35 bg-gradient-to-br from-[#143228] via-[#101F1A] to-[#0E1412]',
    glowClass: 'bg-emerald-500/25',
    iconWrapClass: 'bg-emerald-500/20 text-emerald-300 ring-emerald-400/40',
    textClass: 'text-emerald-50',
    dotActiveClass: 'bg-emerald-400',
  },
  {
    benefit: 'Get Verified Badge',
    Icon: BadgeCheck,
    containerClass:
      'border-amber-500/35 bg-gradient-to-br from-[#3A2E14] via-[#241C10] to-[#16120C]',
    glowClass: 'bg-amber-500/25',
    iconWrapClass: 'bg-amber-500/20 text-amber-300 ring-amber-400/40',
    textClass: 'text-amber-50',
    dotActiveClass: 'bg-amber-400',
  },
  {
    benefit: 'Upload upto 10 images & videos',
    Icon: Image,
    containerClass:
      'border-violet-500/35 bg-gradient-to-br from-[#2A1F45] via-[#1A142C] to-[#12101C]',
    glowClass: 'bg-violet-500/25',
    iconWrapClass: 'bg-violet-500/20 text-violet-300 ring-violet-400/40',
    textClass: 'text-violet-50',
    dotActiveClass: 'bg-violet-400',
  },
  {
    benefit: 'Receive your payments within 2 days',
    Icon: Wallet,
    containerClass:
      'border-cyan-500/35 bg-gradient-to-br from-[#12303A] via-[#0F2228] to-[#0C161A]',
    glowClass: 'bg-cyan-500/25',
    iconWrapClass: 'bg-cyan-500/20 text-cyan-300 ring-cyan-400/40',
    textClass: 'text-cyan-50',
    dotActiveClass: 'bg-cyan-400',
  },
];

const IOS_REDIRECT_MODAL_CONFETTI_COLORS = ['#F55C8D', '#F777A2', '#FFFFFF', '#E5E5EA'];

function fireIosRedirectModalConfetti() {
  const base = { colors: IOS_REDIRECT_MODAL_CONFETTI_COLORS, zIndex: 70 };
  confetti({ ...base, particleCount: 100, spread: 70, origin: { y: 0.45, x: 0.5 } });
  confetti({ ...base, particleCount: 65, angle: 60, spread: 55, origin: { x: 0.12, y: 0.5 } });
  confetti({ ...base, particleCount: 65, angle: 120, spread: 55, origin: { x: 0.88, y: 0.5 } });
}

export const MembershipPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: artistDetails } = useArtistDetailsStore();
  const fetchArtistDetails = useArtistDetailsStore((state) => state.fetchArtistDetails);
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const accessToken = useAuthStore((state) => state.accessToken);
  const revenueCatAppUserId = useRevenueCatAppUserId();
  const subscriptions = artistDetails?.subscriptions;
  const artistCategoryName = artistDetails?.artist_category?.name ?? null;
  const membershipPlans = getMembershipPlansForCategory(artistCategoryName);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annually');
  const [activePlanId, setActivePlanId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [hideOptionsTimeoutId, setHideOptionsTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [processingState, setProcessingState] = useState<{
    planId: string;
    mode: PurchaseMode;
  } | null>(null);
  const [isRestoringPurchases, setIsRestoringPurchases] = useState(false);
  const [eliteEligibility, setEliteEligibility] = useState<EliteEligibility[] | null>(null);
  const [eliteEligibilityLoading, setEliteEligibilityLoading] = useState(false);
  const [eliteEligibilityError, setEliteEligibilityError] = useState<string | null>(null);
  const [now, setNow] = useState<Date>(() => new Date());
  const [isIosRedirectModalOpen, setIsIosRedirectModalOpen] = useState(false);
  const [redirectBannerIndex, setRedirectBannerIndex] = useState(0);
  const [redirectCountdown, setRedirectCountdown] = useState(10);
  const [redirectInFlight, setRedirectInFlight] = useState(false);
  const [redirectAttempted, setRedirectAttempted] = useState(false);
  const [redirectError, setRedirectError] = useState<string | null>(null);
  const [eligibilityConsentState, setEligibilityConsentState] = useState<{
    open: boolean;
    type: EliteEligibilityType | null;
    submitting: boolean;
    agreed: boolean;
  }>({
    open: false,
    type: null,
    submitting: false,
    agreed: false,
  });
  const subscriptionMeta = subscriptions as
    | (typeof subscriptions & { billing_cycle?: string | null; slug?: string | null })
    | null
    | undefined;
  const rawSubscriptionType =
    subscriptions?.subscription_type ??
    subscriptions?.subscription_plan ??
    subscriptionMeta?.billing_cycle ??
    null;
  const normalizedSubscriptionType = String(rawSubscriptionType ?? '')
    .toLowerCase()
    .trim();
  const slug = String(subscriptionMeta?.slug ?? '')
    .toLowerCase()
    .trim();
  const subscriptionType: 'quarterly' | 'yearly' | null =
    normalizedSubscriptionType === 'quarterly'
      ? 'quarterly'
      : normalizedSubscriptionType === 'yearly' || normalizedSubscriptionType === 'annual'
        ? 'yearly'
        : slug.includes('quarter')
          ? 'quarterly'
          : slug.includes('year')
            ? 'yearly'
            : null;
  const isApprovedProfile = artistDetails?.user_info?.profile_status === 'Approved';
  const showMembershipRedirectRibbon = isNativeIOSApp() && isApprovedProfile; //Ribbon is only shown on iOS for approved profiles
  // const showMembershipRedirectRibbon = true;

  type ActiveTier = 'economy' | 'premium' | 'elite' | null;

  const resolvedPlanKey = resolveMembershipPlanKey(subscriptions);
  const activeTier: ActiveTier = resolvedPlanKey === 'free' ? null : resolvedPlanKey;

  const isSupportedSubscriptionType = subscriptionType !== null;
  const hasMountedRef = useRef(false);
  const redirectGenerationRef = useRef(0);
  const previousSubscriptionKeyRef = useRef<string | null>(null);

  const fetchEliteEligibility = async () => {
    try {
      setEliteEligibilityLoading(true);
      setEliteEligibilityError(null);
      const response = await apiClient.get(API_ENDPOINTS.PLANS.ELITE_ELIGIBILITY);
      const data = response.data as {
        success: boolean;
        data?: EliteEligibility[];
      };

      if (!data?.success) {
        throw new Error('Failed to fetch elite eligibility');
      }

      setEliteEligibility(data.data ?? []);
    } catch (error) {
      console.error('Error fetching elite eligibility:', error);
      setEliteEligibilityError('Could not load elite eligibility. Some actions may be limited.');
    } finally {
      setEliteEligibilityLoading(false);
    }
  };

  useEffect(() => {
    void fetchArtistDetails();
    void fetchEliteEligibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (membershipCheckoutUsesIap()) {
      void resolveRevenueCatAppUserId();
    }
  }, []);

  useEffect(() => {
    const refreshMembership = () => {
      void fetchArtistDetails();
      void fetchEliteEligibility();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshMembership();
      }
    };

    window.addEventListener('focus', refreshMembership);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', refreshMembership);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchArtistDetails]);

  useEffect(() => {
    const currentName = subscriptions?.subscription_name?.toLowerCase().trim() ?? 'free';
    const currentType = subscriptionType ?? 'unknown';
    const currentKey = `${currentName}:${currentType}`;

    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousSubscriptionKeyRef.current = currentKey;
      return;
    }

    const previousKey = previousSubscriptionKeyRef.current;
    if (!previousKey || previousKey === currentKey) return;

    previousSubscriptionKeyRef.current = currentKey;

    const displayName =
      currentName.length > 0
        ? `${currentName.charAt(0).toUpperCase()}${currentName.slice(1)}`
        : 'Free';
    const displayCycle = currentType === 'yearly' ? 'Yearly' : currentType === 'quarterly' ? 'Quarterly' : 'Plan';

    confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 } });
    confetti({ particleCount: 80, angle: 60, spread: 55, origin: { x: 0 } });
    confetti({ particleCount: 80, angle: 120, spread: 55, origin: { x: 1 } });
    toast.success(`Congratulations! You are now an ${displayName} ${displayCycle} member.`);
  }, [subscriptions?.subscription_name, subscriptionType]);

  useEffect(() => {
    const hasActiveAccepted = eliteEligibility?.some((entry) => {
      if (entry.status !== 'accepted' || !entry.expired_time) return false;
      const expiry = new Date(entry.expired_time).getTime();
      return expiry > Date.now();
    });

    if (!hasActiveAccepted) return;

    const intervalId = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [eliteEligibility]);

  useEffect(() => {
    if (!isIosRedirectModalOpen) return;
    const sliderIntervalId = setInterval(() => {
      setRedirectBannerIndex((prev) => (prev + 1) % MEMBERSHIP_REDIRECT_BANNER_SLIDES.length);
    }, 2400);
    return () => {
      clearInterval(sliderIntervalId);
    };
  }, [isIosRedirectModalOpen]);

  useEffect(() => {
    if (!isIosRedirectModalOpen) return;
    fireIosRedirectModalConfetti();
    const confettiIntervalId = setInterval(fireIosRedirectModalConfetti, 3500);
    return () => {
      clearInterval(confettiIntervalId);
    };
  }, [isIosRedirectModalOpen]);

  useEffect(() => {
    if (!isIosRedirectModalOpen || redirectInFlight || redirectAttempted) return;
    if (redirectCountdown <= 0) return;
    const countdownIntervalId = setInterval(() => {
      setRedirectCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => {
      clearInterval(countdownIntervalId);
    };
  }, [isIosRedirectModalOpen, redirectInFlight, redirectAttempted, redirectCountdown]);

  useEffect(() => {
    if (!isIosRedirectModalOpen || redirectCountdown !== 0 || redirectAttempted) return;

    const generation = ++redirectGenerationRef.current;
    setRedirectAttempted(true);
    setRedirectInFlight(true);
    setRedirectError(null);

    void (async () => {
      try {
        // One-time link: backend invalidates after use; Retry requests a fresh link.
        const hydratedToken =
          accessToken?.trim() ||
          (await useAuthStore.getState().hydrateAccessTokenFromSecureStorage()) ||
          undefined;
        const redirectUrl = await fetchMembershipWebsiteLinkByPhone(hydratedToken);
        if (generation !== redirectGenerationRef.current) return;

        console.log('[MembershipWorkflowLink] opening url:', redirectUrl);
        if (Capacitor.isNativePlatform()) {
          await Browser.open({
            url: redirectUrl,
            presentationStyle: 'popover',
          });
        } else {
          window.location.assign(redirectUrl);
        }
        setRedirectInFlight(false);
        setIsIosRedirectModalOpen(false);
      } catch (error) {
        if (generation !== redirectGenerationRef.current) return;
        const message =
          error instanceof Error
            ? error.message
            : 'Could not generate website redirect link.';
        setRedirectError(message);
        setRedirectInFlight(false);
        toast.error(message);
      }
    })();
  }, [isIosRedirectModalOpen, redirectAttempted, redirectCountdown, accessToken]);

  const openIosRedirectModal = async () => {
    if (!useAuthStore.getState().accessToken) {
      await useAuthStore.getState().hydrateAccessTokenFromSecureStorage();
    }
    setIsIosRedirectModalOpen(true);
    setRedirectBannerIndex(0);
    setRedirectCountdown(10);
    setRedirectInFlight(false);
    setRedirectAttempted(false);
    setRedirectError(null);
  };

  const closeIosRedirectModal = () => {
    redirectGenerationRef.current += 1;
    setIsIosRedirectModalOpen(false);
    setRedirectInFlight(false);
    setRedirectAttempted(false);
    setRedirectError(null);
  };

  const retryIosRedirect = () => {
    // Each retry creates a new workflow-short-link (links are single-use).
    redirectGenerationRef.current += 1;
    setRedirectCountdown(10);
    setRedirectAttempted(false);
    setRedirectInFlight(false);
    setRedirectError(null);
  };

  const openEligibilityModal = (type: EliteEligibilityType) => {
    setEligibilityConsentState({
      open: true,
      type,
      submitting: false,
      agreed: false,
    });
  };

  const closeEligibilityModal = () => {
    setEligibilityConsentState((prev) => ({
      ...prev,
      open: false,
      submitting: false,
    }));
  };

  const handleEligibilityConsentChange = (checked: boolean) => {
    setEligibilityConsentState((prev) => ({
      ...prev,
      agreed: checked,
    }));
  };

  const handleSubmitEligibility = async () => {
    if (!eligibilityConsentState.type) return;

    if (!eligibilityConsentState.agreed) {
      toast.error('Please certify that the information provided is true and correct.');
      return;
    }

    try {
      setEligibilityConsentState((prev) => ({
        ...prev,
        submitting: true,
      }));

      await apiClient.post(API_ENDPOINTS.PLANS.ELITE_ELIGIBILITY, {
        type: eligibilityConsentState.type,
      });

      toast.success('Elite eligibility request submitted.');
      closeEligibilityModal();
      void fetchEliteEligibility();
    } catch (error) {
      console.error('Error submitting elite eligibility:', error);
      toast.error('Could not submit eligibility request. Please try again.');
      setEligibilityConsentState((prev) => ({
        ...prev,
        submitting: false,
      }));
    }
  };

  const getEliteEligibilityEntry = (): EliteEligibility | null => {
    // API returns either [] or a single entry.
    if (!eliteEligibility || eliteEligibility.length === 0) return null;
    return eliteEligibility[0] ?? null;
  };

  const getCountdownForEligibility = (entry: EliteEligibility | null): string | null => {
    if (!entry?.expired_time) return null;

    const expiry = new Date(entry.expired_time).getTime();
    const diffMs = expiry - now.getTime();

    if (Number.isNaN(expiry) || diffMs <= 0) {
      return null;
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (value: number) => value.toString().padStart(2, '0');

    return `${pad(days)} : ${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);

    return () => {
      window.removeEventListener('resize', updateIsMobile);
    };
  }, []);

  useEffect(() => {
    if (!isMobile || !activePlanId) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      if (target.closest('[data-buy-options-container]')) {
        return;
      }

      // If a purchase is in progress for this plan, do not hide the options
      if (processingState && processingState.planId === activePlanId) {
        return;
      }

      setActivePlanId(null);
      if (hideOptionsTimeoutId) {
        clearTimeout(hideOptionsTimeoutId);
        setHideOptionsTimeoutId(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isMobile, activePlanId, hideOptionsTimeoutId]);

  const getPrice = (plan: Plan) => {
    return getPlanDisplayPrice(
      plan,
      billingPeriod === 'annually' ? 'annually' : 'quarterly',
      artistCategoryName,
    );
  };

  const getSavings = (plan: Plan) => {
    return billingPeriod === 'annually' ? plan.annualSavings : plan.quarterlySavings;
  };

  const getEmiAmount = (plan: Plan): number | null =>
    getPlanEmiAmount(
      plan,
      billingPeriod === 'annually' ? 'annually' : 'quarterly',
    );

  type PurchaseMode = 'full' | 'emi';

  const usesAppleIap = membershipCheckoutUsesIap();
  const showEmiOption = membershipEmiAvailable();
  const {
    data: iosPrices,
    isLoading: iosPricesLoading,
  } = useIosSubscriptionPrices(revenueCatAppUserId);

  useEffect(() => {
    if (usesAppleIap && revenueCatAppUserId) {
      void queryClient.invalidateQueries({ queryKey: [IOS_SUBSCRIPTION_PRICES_KEY] });
    }
  }, [usesAppleIap, revenueCatAppUserId, queryClient]);

  const handleBuyNow = async (plan: Plan, mode: PurchaseMode) => {
    const selectedPlanId =
      billingPeriod === 'annually' ? plan.annualPlanId : plan.quarterlyPlanId;

    if (!selectedPlanId) {
      toast.error('Plan configuration is missing. Please contact support.');
      return;
    }

    setProcessingState({ planId: plan.id, mode });

    let appUserId =
      revenueCatAppUserId ?? (await resolveRevenueCatAppUserId()) ?? undefined;
    if (usesAppleIap && !appUserId) {
      setProcessingState(null);
      if (!isAuthenticated && !hasValidAuthCookies()) {
        toast.error('Please sign in to subscribe with Apple.');
      } else {
        toast.error(
          'Could not verify your account. Pull to refresh or try again.',
        );
      }
      return;
    }

    const emiAmount = getEmiAmount(plan);
    const price = getPrice(plan);
    if (!usesAppleIap) {
      setActivePlanId(plan.id);
    }

    const loadingToast = usesAppleIap
      ? undefined
      : toast.loading('Initiating membership purchase...');

    const customerName = user?.name || 'Artist';
    const customerEmail = user?.email || '';
    const customerPhone = user?.phone || '';

    try {
      await purchaseMembership({
        backendPlanId: selectedPlanId,
        categoryName: artistCategoryName,
        mode,
        customerName,
        customerEmail,
        customerPhone,
        emiAmountInr: emiAmount,
        fullPriceInr: price,
        appUserId: appUserId ?? '',
        onBeforeStoreKit: () => {
          if (loadingToast) toast.dismiss(loadingToast);
        },
        onSuccess: async () => {
          if (loadingToast) toast.dismiss(loadingToast);
          setProcessingState(null);
          toast.success(
            usesAppleIap
              ? 'Subscription active! It may take a moment to reflect in your account.'
              : mode === 'emi'
                ? 'Membership EMI payment successful!'
                : 'Membership payment successful!',
          );
          await fetchArtistDetails();
        },
        onFailure: (error) => {
          if (loadingToast) toast.dismiss(loadingToast);
          setProcessingState(null);
          if (error.code === 'PAYMENT_CANCELLED') {
            toast.error('Payment cancelled');
          } else {
            toast.error(error.description || 'Payment failed');
          }
        },
      });
      if (loadingToast) toast.dismiss(loadingToast);
    } catch (error) {
      console.error('Membership purchase error:', error);
      const err = error as { code?: string; message?: string };
      if (err.code === 'PAYMENT_CANCELLED') {
        toast.error('Payment cancelled');
      } else {
        toast.error(
          err.message || 'Could not initiate membership purchase. Please try again.',
        );
      }
      setProcessingState(null);
    }
  };

  const handleRestorePurchases = async () => {
    const appUserId =
      revenueCatAppUserId ?? (await resolveRevenueCatAppUserId()) ?? undefined;
    if (!appUserId) {
      if (!isAuthenticated && !hasValidAuthCookies()) {
        toast.error('Please sign in to restore purchases.');
      } else {
        toast.error(
          'Could not verify your account. Pull to refresh or try again.',
        );
      }
      return;
    }
    if (!isRevenueCatEnabled()) {
      toast.error('Restore purchases is only available on the iOS app.');
      return;
    }

    setIsRestoringPurchases(true);
    const loadingToast = toast.loading('Restoring purchases…');
    try {
      await restoreMembershipPurchases(appUserId);
      toast.dismiss(loadingToast);
      toast.success('Purchases restored. Refreshing your membership…');
      await fetchArtistDetails();
    } catch (error) {
      console.error('Restore purchases error:', error);
      toast.dismiss(loadingToast);
      toast.error(
        error instanceof Error ? error.message : 'Could not restore purchases. Try again later.',
      );
    } finally {
      setIsRestoringPurchases(false);
    }
  };

  const isActivePlanForCurrentView = (plan: Plan): boolean => {
    if (!activeTier || !isSupportedSubscriptionType) return false;
    if (plan.id !== activeTier) return false;

    if (subscriptionType === 'quarterly' && billingPeriod === 'quarterly') return true;
    if (subscriptionType === 'yearly' && billingPeriod === 'annually') return true;

    return false;
  };

  const shouldHideBuyNow = (plan: Plan): boolean => {
    if (!activeTier || !isSupportedSubscriptionType) return false;

    const isEconomyPlan = plan.id === 'economy';
    const isPremiumPlan = plan.id === 'premium';
    const isElitePlan = plan.id === 'elite';

    if (activeTier === 'economy') {
      // Economy: hide Buy Now on Economy for both billing periods
      if (isEconomyPlan) return true;
      return false;
    }

    if (activeTier === 'premium') {
      // Premium: hide Buy Now on Economy and Premium for both billing periods
      if (isEconomyPlan || isPremiumPlan) return true;
      return false;
    }

    if (activeTier === 'elite') {
      // Elite:
      // - Economy and Premium: hide Buy Now for both billing periods
      // - If active is Elite Quarterly, keep Annual buy visible for upgrade path.
      if (isEconomyPlan || isPremiumPlan) return true;
      if (isElitePlan) {
        if (subscriptionType === 'quarterly' && billingPeriod === 'annually') {
          return false;
        }
        return true;
      }
    }

    return false;
  };

  const shouldShowPlanPurchaseCta = (
    plan: Plan,
    eliteIsAcceptedForPlan: boolean,
  ): boolean => {
    const isElitePlan = plan.id === 'elite';
    return !shouldHideBuyNow(plan) && (!isElitePlan || eliteIsAcceptedForPlan);
  };

  const shouldDeEmphasizePlan = (plan: Plan): boolean => {
    if (!activeTier || !subscriptionType) return false;

    const isEconomyPlan = plan.id === 'economy';
    const isPremiumPlan = plan.id === 'premium';
    const isElitePlan = plan.id === 'elite';

    // Free users and Economy Quarterly users should see all cards normally.
    if (activeTier === 'economy' && subscriptionType === 'quarterly') return false;

    if (activeTier === 'economy' && subscriptionType === 'yearly') {
      // Dim only Economy Quarterly card (cross-cycle downgrade feel).
      return isEconomyPlan && billingPeriod === 'quarterly';
    }

    if (activeTier === 'premium' && subscriptionType === 'quarterly') {
      // Dim Economy on both tabs, and Premium Annual.
      if (isEconomyPlan) return true;
      if (isPremiumPlan && billingPeriod === 'annually') return true;
      return false;
    }

    if (activeTier === 'premium' && subscriptionType === 'yearly') {
      // Dim Economy on both tabs, and Premium Quarterly.
      if (isEconomyPlan) return true;
      if (isPremiumPlan && billingPeriod === 'quarterly') return true;
      return false;
    }

    if (activeTier === 'elite' && subscriptionType === 'quarterly') {
      // Dim Economy/Premium on both tabs, and Elite Annual.
      if (isEconomyPlan || isPremiumPlan) return true;
      if (isElitePlan && billingPeriod === 'annually') return true;
      return false;
    }

    if (activeTier === 'elite' && subscriptionType === 'yearly') {
      // Dim Economy/Premium on both tabs, and Elite Quarterly.
      if (isEconomyPlan || isPremiumPlan) return true;
      if (isElitePlan && billingPeriod === 'quarterly') return true;
      return false;
    }

    return false;
  };

  const openMobileOptions = (planId: string) => {
    setActivePlanId(planId);

    if (hideOptionsTimeoutId) {
      clearTimeout(hideOptionsTimeoutId);
    }

    // If there is an ongoing purchase for this plan, do not auto-hide
    if (processingState && processingState.planId === planId) {
      setHideOptionsTimeoutId(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      // If a purchase started during the timeout for this plan, keep it open
      if (processingState && processingState.planId === planId) {
        setHideOptionsTimeoutId(null);
        return;
      }
      setActivePlanId(null);
      setHideOptionsTimeoutId(null);
    }, 10000);

    setHideOptionsTimeoutId(timeoutId);
  };

  const handlePreview = (planId: Plan['id']) => {
    navigate(`/preview-my-website?plan=${planId}`);
  };

  return (
    <div className="w-full min-h-screen bg-[#0B0B0D] text-white">
      <div className="w-full md:px-4 py-10 pb-24">
        {/* Header */}
        <div className="mb-8 lg:mb-12">
          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-2xl">
              <Star className="h-8 w-8 text-[#8E8E93]" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-black text-white tracking-tight tracking-wide">Membership</h1>
          </div>
          <p className="text-[#8E8E93] text-base lg:text-lg font-medium">
            Choose a membership plan that fits your needs and elevate your career
          </p>
          {usesAppleIap && (
            <button
              type="button"
              onClick={() => void handleRestorePurchases()}
              disabled={isRestoringPurchases}
              className="mt-4 text-sm font-medium text-[#F33363] hover:text-[#F55C8D] disabled:opacity-50"
            >
              {isRestoringPurchases ? 'Restoring…' : 'Restore purchases'}
            </button>
          )}
        </div>

        {/* Billing Period Tabs + Compare plans – same row */}
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4 border-b border-[#3A3A3C] pb-0">
          <nav className="flex gap-6 lg:gap-8" aria-label="Billing period">
            <button
              type="button"
              onClick={() => setBillingPeriod('quarterly')}
              className={cn(
                'relative pb-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm',
                billingPeriod === 'quarterly'
                  ? 'text-white'
                  : 'text-[#8E8E93] hover:text-white'
              )}
              aria-current={billingPeriod === 'quarterly' ? 'page' : undefined}
            >
              Quarterly
              {billingPeriod === 'quarterly' && (
                <motion.span
                  layoutId="membership-tab-underline"
                  className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#F33363] rounded-full"
                  transition={{ type: 'spring', duration: 0.3 }}
                />
              )}
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod('annually')}
              className={cn(
                'relative pb-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm',
                billingPeriod === 'annually'
                  ? 'text-white'
                  : 'text-[#8E8E93] hover:text-white'
              )}
              aria-current={billingPeriod === 'annually' ? 'page' : undefined}
            >
              Annually
              {billingPeriod === 'annually' && (
                <motion.span
                  layoutId="membership-tab-underline"
                  className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#F33363] rounded-full"
                  transition={{ type: 'spring', duration: 0.3 }}
                />
              )}
            </button>
          </nav>
          <button
            type="button"
            onClick={() => navigate('/compare-plans')}
            className="flex justify-center items-center gap-1.5  py-2 px-4 rounded-br-none text-sm font-medium border border-[#3A3A3C] text-[#8E8E93] hover:text-[#F33363] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm"
          >
            <GitCompare className="h-3.5 w-3.5" />
            Compare plans
          </button>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
          {membershipPlans.map((plan, index) => {
            const savings = getSavings(plan);
            const emiAmount = getEmiAmount(plan);
            const isEconomy = plan.id === 'economy';
            const isPremium = plan.id === 'premium';
            const isElite = plan.id === 'elite';

            const eliteEligibilityForView: EliteEligibility | null = isElite ? getEliteEligibilityEntry() : null;

            const eliteCountdown = getCountdownForEligibility(eliteEligibilityForView);
            const eliteIsAccepted =
              eliteEligibilityForView?.status === 'accepted' && Boolean(eliteCountdown);
            const eliteIsPending = eliteEligibilityForView?.status === 'pending';

            const isPlanProcessing =
              processingState !== null && processingState.planId === plan.id;
            const isEmiProcessing =
              isPlanProcessing && processingState?.mode === 'emi';
            const isFullProcessing =
              isPlanProcessing && processingState?.mode === 'full';
            const isDeEmphasized = shouldDeEmphasizePlan(plan);
            const majorFeatures = billingPeriod === 'quarterly' ? plan.quarterlyMajorFeatures : plan.annualMajorFeatures;
            const features = billingPeriod === 'quarterly' ? plan.quarterlyFeatures : plan.annualFeatures;

            // Define gradient classes based on plan type
            let gradientClass = '';
            let blurClass = '';
            let iconBgClass = '';
            let accentColor = '';

            if (isEconomy) {
              gradientClass = 'bg-gradient-to-br from-[#1C1C1E] via-[#1C1C1E] to-[#2C2C2E]/30';
              blurClass = 'bg-[#3A3A3C]/10';
              iconBgClass = 'bg-[#2C2C2E]';
              accentColor = 'text-[#8E8E93]';
            } else if (isPremium) {
              gradientClass = 'bg-gradient-to-br from-[#1C1C1E] via-[#1C1C1E] to-amber-500/10';
              blurClass = 'bg-amber-500/5';
              iconBgClass = 'bg-amber-500/10';
              accentColor = 'text-amber-500';
            } else if (isElite) {
              gradientClass = 'bg-gradient-to-br from-[#1C1C1E] via-[#1C1C1E] to-pink-500/10';
              blurClass = 'bg-pink-500/5';
              iconBgClass = 'bg-pink-500/10';
              accentColor = 'text-pink-400';
            }

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={cn(
                  'relative rounded-[2rem] border flex flex-col shadow-2xl',
                  gradientClass,
                  isElite
                    ? 'border-[#3A3A3C]'
                    : 'border-[#3A3A3C]/50 hover:border-[#3A3A3C]',
                  plan.isPopular && 'lg:scale-105',
                  isDeEmphasized &&
                    'saturate-0 opacity-70 border-[#2A2A2C] hover:border-[#2A2A2C]'
                )}
              >
                {/* Popular Badge - only for premium, and only when this card is not the user's active plan */}
                {plan.isPopular && !isActivePlanForCurrentView(plan) && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 px-5 py-1.5 bg-gradient-to-r from-[#1C1C1E] via-[#1C1C1E] to-[#2C2C2E]/30 border border-[#1C1C1E] rounded-full text-xs font-black text-[#8E8E93] uppercase tracking-wider shadow-lg">
                    Most Popular
                  </div>
                )}

                <div className="relative overflow-hidden rounded-[2rem] flex flex-col flex-1">
                  {/* Blur effect */}
                  <div className={cn('absolute top-0 right-0 w-40 h-40 blur-3xl rounded-full -mr-20 -mt-20', blurClass)} />

                  {/* Scrollable card body: sticky banner + content */}
                  <div className="relative z-10 flex-1 min-h-0 flex flex-col">
                    {/* Sticky "Active plan" banner - only for current plan */}
                    {isActivePlanForCurrentView(plan) && (
                      <div
                        className={cn(
                          'sticky top-0 z-20 shrink-0 flex items-center justify-center gap-3 px-4 py-1 lg:py-1 ',
                          'ring-b ring-1 shadow-lg backdrop-blur-sm',
                          'bg-gradient-to-r',
                          isPremium
                            ? 'from-amber-500/20 via-amber-500/15 to-amber-500/10 ring-amber-500/40 shadow-amber-500/10'
                            : isElite
                              ? 'from-pink-500/20 via-pink-500/15 to-pink-500/10 ring-pink-500/40 shadow-pink-500/10'
                              : 'from-emerald-500/20 via-emerald-500/15 to-emerald-500/10 ring-emerald-500/40 shadow-emerald-500/10'
                        )}
                      >
                        <div
                          className={cn(
                            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                            isPremium
                              ? 'bg-amber-500/25 text-amber-400'
                              : isElite
                                ? 'bg-pink-500/25 text-pink-400'
                                : 'bg-emerald-500/25 text-emerald-400'
                          )}
                        >
                          <Check className="h-4 w-4 stroke-[2.5]" />
                        </div>
                        <div className="flex flex-col items-center text-center sm:flex-col sm:gap-0 sm:text-left">
                          <span
                            className={cn(
                              'text-xs font-semibold uppercase tracking-wider',
                              isPremium
                                ? 'text-amber-400/90'
                                : isElite
                                  ? 'text-pink-400/90'
                                  : 'text-emerald-400/90'
                            )}
                          >
                            Active plan
                          </span>
                          <span className="text-sm lg:text-base font-semibold text-white">
                            {subscriptions?.subscription_name} ·{' '}
                            {subscriptionType === 'yearly' ? 'Annual' : 'Quarterly'}
                          </span>
                        </div>
                      </div>
                    )}

                    {showMembershipRedirectRibbon && shouldShowPlanPurchaseCta(plan, eliteIsAccepted) && (
                      <div className="shrink-0 px-4 lg:px-6 pt-4">
                        <div
                          className="inline-flex w-full max-w-full items-center gap-2 min-h-[44px] px-4 py-3 pr-7 text-xs sm:text-sm text-[#E5E5EA] bg-gradient-to-r from-[#1A1A1D] via-[#1F1F24] to-[#24242A]"
                          style={{
                            clipPath:
                              'polygon(0 0, calc(100% - 16px) 0, 100% 50%, calc(100% - 16px) 100%, 0 100%)',
                          }}
                        >
                          <ArrowBigRightDash
                            className="h-5 w-5 shrink-0 text-[#F55C8D]"
                            aria-hidden
                          />
                          <span className="min-w-0">
                            <button
                              type="button"
                              className="font-semibold underline underline-offset-2 text-[#F55C8D] hover:text-[#F777A2]"
                              onClick={() => void openIosRedirectModal()}
                            >
                              Click here
                            </button>{' '}
                            to get upto 20% off
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Plan Header */}
                    <div className="relative z-10 p-6 lg:p-8 pb-4">
                      <div className="flex items-start justify-between mb-6">
                        <div className={cn('w-14 h-14 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center', iconBgClass)}>
                          <Rocket className={cn('h-7 w-7 lg:h-8 lg:w-8', accentColor)} />
                        </div>
                        <div className="text-right pl-2">
                          <div className={cn(
                            'px-3 py-1.5 rounded-full text-xs font-bold border',
                            isPremium ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' :
                              isElite ? 'bg-pink-500/10 border-pink-500/30 text-pink-400' :
                                'bg-[#252527] border-[#3A3A3C] text-[#8E8E93]'
                          )}>
                            {plan.starCoinsDiscount}
                          </div>
                        </div>
                      </div>

                      <h2 className="text-3xl lg:text-4xl font-black text-white mb-2 tracking-tight">{plan.name}</h2>
                      <p className="text-sm text-[#8E8E93] mb-6 font-medium">{plan.tagline}</p>

                      {/* Pricing */}
                      <div className="mb-4">
                        {savings && (
                          <span className={cn(
                            'inline-block px-3 py-1.5 rounded-lg text-xs font-bold mb-3 border',
                            isPremium ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' :
                              isElite ? 'bg-pink-500/10 border-pink-500/30 text-pink-400' :
                                'bg-[#252527] border-[#3A3A3C] text-[#8E8E93]'
                          )}>
                            {savings}
                          </span>
                        )}
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <MembershipPlanPrice
                            plan={plan}
                            billingPeriod={billingPeriod === 'annually' ? 'annually' : 'quarterly'}
                            categoryName={artistCategoryName}
                            usesAppleIap={usesAppleIap}
                            iosPrices={iosPrices}
                            iosPricesLoading={iosPricesLoading}
                          />
                          {showEmiOption && emiAmount != null && (
                            <span
                              className="inline-block py-1.5 pl-3 pr-5 text-xs font-semibold tracking-wide text-[#E5E5EA] bg-[#2C2C2E]  shadow-sm"
                              style={{
                                clipPath:
                                  'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)',
                              }}
                            >
                              EMI @ ₹{emiAmount.toLocaleString()}/mo
                            </span>
                          )}
                        </div>
                        {billingPeriod === 'annually' && (
                          <p className="text-xs text-[#8E8E93] mt-3 font-medium">
                            {plan.annualSavings} with an annual subscription!
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Major Features Grid */}
                    <div className="relative z-10 px-6 lg:px-8 pb-4">
                      <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-2 gap-2 lg:gap-3">
                        {majorFeatures.map((feature, featureIndex) => {
                          const Icon = feature.icon;
                          const isMediaUpload = feature.label === 'Media Uploads';

                          let imageCount: string | null = null;
                          let videoCount: string | null = null;

                          if (isMediaUpload) {
                            const match = feature.value.match(/(\d+)\s*Images?,\s*(\d+)\s*Videos?/i);
                            if (match) {
                              imageCount = match[1];
                              videoCount = match[2];
                            }
                          }

                          return (
                            <div
                              key={featureIndex}
                              className={cn(
                                'aspect-square rounded-xl lg:rounded-2xl p-2.5 lg:p-3 border transition-all flex flex-col items-center justify-center text-center',
                                isPremium
                                  ? 'bg-amber-500/5 border-amber-500/5 hover:bg-amber-500/10'
                                  : isElite
                                    ? 'bg-pink-500/5 border-pink-500/5 hover:bg-pink-500/10'
                                    : 'bg-[#1C1C1E] border-[#3A3A3C]/30 hover:border-[#3A3A3C]'
                              )}
                            >
                              <div
                                className={cn(
                                  'w-7 h-7 lg:w-9 lg:h-9 rounded-lg lg:rounded-xl flex items-center justify-center mb-1.5 lg:mb-2',
                                  isPremium
                                    ? 'bg-amber-500/10'
                                    : isElite
                                      ? 'bg-pink-500/10'
                                      : 'bg-[#2C2C2E]'
                                )}
                              >
                                <Icon
                                  className={cn(
                                    'h-3.5 w-3.5 lg:h-4 lg:w-4',
                                    isPremium
                                      ? 'text-amber-200'
                                      : isElite
                                        ? 'text-pink-200'
                                        : 'text-[#8E8E93]'
                                  )}
                                />
                              </div>
                              <p className="text-[10px] lg:text-xs text-[#8E8E93] font-medium mb-0.5 lg:mb-1 line-clamp-1">
                                {feature.label}
                              </p>
                              {isMediaUpload && imageCount && videoCount ? (
                                <div className="flex items-center justify-center gap-3 text-[11px] lg:text-xs text-white font-bold">
                                  <span className="inline-flex items-center gap-1">
                                    <Image className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-[#E5E7EB]" />
                                    {imageCount}
                                  </span>
                                  <span className="inline-flex items-center gap-1">
                                    <Video className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-[#E5E7EB]" />
                                    {videoCount}
                                  </span>
                                </div>
                              ) : (
                                <p className="text-[11px] lg:text-xs text-white font-bold line-clamp-2">
                                  {feature.value}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Other Features List */}
                    <div className="relative z-10 flex-1 px-6 lg:px-8 pb-6">
                      <div className="space-y-2.5 max-h-[200px] overflow-y-auto pr-2">
                        {features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-start gap-2.5">
                            <div
                              className={cn(
                                'w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                                isPremium
                                  ? 'bg-amber-500/20'
                                  : isElite
                                    ? 'bg-pink-500/20'
                                    : 'bg-[#3A3A3C]'
                              )}
                            >
                              <Check
                                className={cn(
                                  'h-2.5 w-2.5',
                                  isPremium
                                    ? 'text-amber-400'
                                    : isElite
                                      ? 'text-pink-400'
                                      : 'text-[#8E8E93]'
                                )}
                              />
                            </div>
                            <p className="text-xs text-[#8E8E93] leading-relaxed">
                              {feature.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="relative z-10 p-6 lg:p-8 pt-4 border-t border-[#3A3A3C]/50 space-y-3">
                      <button
                        onClick={() => handlePreview(plan.id)}
                        className={cn(
                          'w-full flex items-center justify-center gap-2 text-sm font-medium transition-colors rounded-xl py-2',
                          isPremium ? 'text-amber-400 hover:text-amber-300' :
                            isElite ? 'text-pink-400 hover:text-pink-300' :
                              'text-[#8E8E93] hover:text-white'
                        )}
                      >
                        Preview the {plan.name} Website
                        <ExternalLink className="h-4 w-4" />
                      </button>
                      {eliteEligibilityError && isElite && (
                        <p className="text-[11px] text-center text-amber-400 mb-1">
                          {eliteEligibilityError}
                        </p>
                      )}

                      {shouldShowPlanPurchaseCta(plan, eliteIsAccepted) && (
                          <div
                            data-buy-options-container
                            onMouseEnter={
                              !usesAppleIap && !isMobile
                                ? () => setActivePlanId(plan.id)
                                : undefined
                            }
                            onMouseLeave={
                              !usesAppleIap && !isMobile
                                ? () => {
                                  if (processingState?.planId === plan.id) return;
                                  setActivePlanId(null);
                                }
                                : undefined
                            }
                          >
                            {usesAppleIap ? (
                              <>
                                <Button
                                  type="button"
                                  variant="active"
                                  className="w-full py-4 lg:py-4.5 text-base lg:text-lg font-bold rounded-2xl bg-[#F55C8D] hover:bg-[#F33363] active:bg-[#D92D58]"
                                  disabled={isPlanProcessing}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (!isPlanProcessing) {
                                      void handleBuyNow(plan, 'full');
                                    }
                                  }}
                                >
                                  {isPlanProcessing ? (
                                    <span className="flex items-center justify-center gap-2">
                                      <Loader size="small" />
                                      Preparing...
                                    </span>
                                  ) : (
                                    'Buy Now'
                                  )}
                                </Button>
                                <IosSubscriptionLegalDisclosure
                                  linksOnly
                                  className="mt-3 px-1"
                                />
                              </>
                            ) : (
                            <AnimatePresence initial={false} mode="wait">
                              {activePlanId === plan.id ? (
                                <motion.div
                                  key="buy-options"
                                  layout
                                  initial={{ scale: 0.98 }}
                                  animate={{ scale: 1 }}
                                  exit={{ scale: 0.98 }}
                                  transition={{
                                    duration: 0.2,
                                    ease: 'easeInOut',
                                  }}
                                  className="w-full"
                                >
                                  <div className="grid grid-cols-1 gap-3">
                                    {showEmiOption && (
                                    <Button
                                      variant="secondary"
                                      className="w-full py-3 lg:py-3.5 text-sm lg:text-base font-semibold rounded-2xl border border-[#3A3A3C]/80 bg-white/90 text-black hover:bg-white/70"
                                      onClick={() => !isPlanProcessing && handleBuyNow(plan, 'emi')}
                                      disabled={isPlanProcessing}
                                    >
                                      {isEmiProcessing ? (
                                        <span className="flex items-center justify-center gap-2 text-black">
                                          <span className="inline-flex items-center justify-center rounded-full bg-black/10 p-1">
                                            <Loader size="small" />
                                          </span>
                                          <span>Processing...</span>
                                        </span>
                                      ) : emiAmount ? (
                                        `Pay ₹${emiAmount.toLocaleString()}/month with EMI`
                                      ) : (
                                        'Pay monthly with EMI'
                                      )}
                                    </Button>
                                    )}
                                    <Button
                                      variant="active"
                                      className="w-full py-3 lg:py-3.5 text-sm lg:text-base font-bold rounded-2xl bg-[#F55C8D] hover:bg-[#F33363] active:bg-[#D92D58]"
                                      onClick={() => !isPlanProcessing && handleBuyNow(plan, 'full')}
                                      disabled={isPlanProcessing}
                                    >
                                      {isFullProcessing ? (
                                        <span className="flex items-center justify-center gap-2">
                                          <Loader size="small" />
                                          Processing...
                                        </span>
                                      ) : (
                                        'Pay in Full'
                                      )}
                                    </Button>
                                  </div>
                                </motion.div>
                              ) : (
                                <motion.div
                                  key="buy-primary"
                                  layout
                                  initial={{ scale: 0.98 }}
                                  animate={{ scale: 1 }}
                                  exit={{ scale: 0.98 }}
                                  transition={{
                                    duration: 0.2,
                                    ease: 'easeInOut',
                                  }}
                                  className="w-full"
                                >
                                  <Button
                                    onClick={(event) => {
                                      if (isMobile) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        openMobileOptions(plan.id);
                                      } else {
                                        setActivePlanId(plan.id);
                                      }
                                    }}
                                    variant="active"
                                    className="w-full py-4 lg:py-4.5 text-base lg:text-lg font-bold rounded-2xl bg-[#F55C8D] hover:bg-[#F33363] active:bg-[#D92D58]"
                                  >
                                    Buy Now
                                  </Button>
                                </motion.div>
                              )}
                            </AnimatePresence>
                            )}
                          </div>
                      )}
                      {isElite && activeTier !== 'elite' && (
                          <div className="mt-2 space-y-1">
                            {eliteIsAccepted && eliteCountdown && (
                              <p className="text-xs text-center text- WHITE font-semibold">
                                Offer expires in
                              </p>
                            )}
                            <p className="text-MD text-center text-pink-400 font-semibold">
                              {eliteCountdown}
                            </p>
                            {eliteIsPending && (
                              <Button
                                type="button"
                                variant="active"
                                aria-disabled
                                className="w-full py-3 text-sm font-semibold rounded-2xl bg-[#F55C8D] hover:bg-[#F55C8D] active:bg-[#F55C8D] cursor-not-allowed opacity-70"
                                onClick={() =>
                                  toast(
                                    'Your eligibility request has been submitted. Please wait for an update.',
                                    { icon: 'ℹ️' }
                                  )
                                }
                              >
                                Eligibility Request Submitted
                              </Button>
                            )}
                            {!eliteIsAccepted && !eliteIsPending && (
                              <Button
                                type="button"
                                variant="active"
                                className="w-full py-3 text-sm font-semibold rounded-2xl bg-[#F55C8D] hover:bg-[#F33363] active:bg-[#D92D58]"
                                disabled={eliteEligibilityLoading}
                                onClick={() =>
                                  openEligibilityModal(
                                    billingPeriod === 'annually' ? 'annually' : 'quarterly'
                                  )
                                }
                              >
                                Check Eligibility
                              </Button>
                            )}
                          </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
      <AnimatePresence>
        {isIosRedirectModalOpen && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 backdrop-blur-sm px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeIosRedirectModal}
          >
            <motion.div
              className="w-full max-w-md rounded-3xl border border-[#3A3A3C]/60 p-5 lg:p-6 bg-[linear-gradient(160deg,_#0A0A0C_0%,_#131319_45%,_#1A1A22_100%)] shadow-2xl"
              initial={{ y: 24, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 24, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.22 }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#8E8E93]">
                    Membership perks
                  </p>
                  <h2 className="text-xl lg:text-2xl font-black text-white mt-1">
                    Unlock Premium Benefits
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={closeIosRedirectModal}
                  disabled={redirectInFlight}
                  className="h-8 w-8 rounded-full border border-[#3A3A3C] text-[#8E8E93] hover:text-white hover:border-[#5A5A5E] disabled:opacity-50"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="mt-5 overflow-hidden min-h-[148px]">
                <motion.div
                  className="flex"
                  animate={{ x: `-${redirectBannerIndex * 100}%` }}
                  transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
                >
                  {MEMBERSHIP_REDIRECT_BANNER_SLIDES.map((slide, index) => {
                    const SlideIcon = slide.Icon;
                    return (
                      <div
                        key={index}
                        className={cn(
                          'relative w-full shrink-0 flex-none overflow-hidden rounded-2xl border px-4 py-6 min-h-[148px]',
                          'flex flex-col items-center justify-center gap-3 text-center shadow-lg',
                          slide.containerClass,
                        )}
                      >
                        <div
                          className={cn(
                            'pointer-events-none absolute -right-6 -top-8 h-28 w-28 rounded-full blur-2xl',
                            slide.glowClass,
                          )}
                          aria-hidden
                        />
                        <div
                          className={cn(
                            'pointer-events-none absolute -bottom-10 -left-6 h-24 w-24 rounded-full blur-2xl opacity-70',
                            slide.glowClass,
                          )}
                          aria-hidden
                        />
                        <div
                          className={cn(
                            'relative z-10 flex h-12 w-12 items-center justify-center rounded-2xl ring-1 shadow-md',
                            slide.iconWrapClass,
                          )}
                        >
                          <SlideIcon className="h-6 w-6" strokeWidth={2.25} aria-hidden />
                        </div>
                        <p
                          className={cn(
                            'relative z-10 max-w-[280px] text-base lg:text-lg font-bold leading-snug tracking-tight',
                            slide.textClass,
                          )}
                        >
                          {slide.benefit}
                        </p>
                      </div>
                    );
                  })}
                </motion.div>
              </div>

              <div className="mt-3 flex items-center justify-center gap-1.5">
                {MEMBERSHIP_REDIRECT_BANNER_SLIDES.map((slide, index) => (
                  <span
                    key={index}
                    className={cn(
                      'h-1.5 rounded-full transition-all duration-300',
                      index === redirectBannerIndex
                        ? cn('w-6', slide.dotActiveClass)
                        : 'w-1.5 bg-[#4B4B50]',
                    )}
                  />
                ))}
              </div>

              <div className="mt-6 rounded-2xl border border-[#2E2E33] bg-[#111116] px-4 py-4 text-center">
                <p className="text-sm text-[#A1A1AA] font-medium">Redirecting to Website</p>
                <p className="mt-1 text-3xl font-black tracking-widest text-white tabular-nums">
                  {redirectCountdown}
                </p>
                {redirectInFlight && (
                  <p className="mt-2 text-xs text-[#8E8E93]">Generating secure link...</p>
                )}
                {redirectError && (
                  <p className="mt-2 text-xs text-amber-400">{redirectError}</p>
                )}
              </div>

              <div className="mt-4 flex gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1 py-2.5 rounded-xl border border-[#3A3A3C] bg-transparent text-[#E5E5EA] hover:bg-[#2C2C2E]"
                  onClick={closeIosRedirectModal}
                  disabled={redirectInFlight}
                >
                  Close
                </Button>
                <Button
                  type="button"
                  variant="active"
                  className="flex-1 py-2.5 rounded-xl bg-[#F55C8D] hover:bg-[#F33363] active:bg-[#D92D58] disabled:opacity-70"
                  onClick={retryIosRedirect}
                  disabled={redirectInFlight}
                >
                  Retry
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {eligibilityConsentState.open && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="w-full max-w-lg rounded-2xl border border-[#3A3A3C]/50 p-6 space-y-4 bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)]"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <h2 className="text-xl font-bold text-white/80 mb-2 border-b border-[#3A3A3C]/80 pb-2">
                Artist Elite Membership: Eligibility Consent
              </h2>
              <div className="space-y-3 text-sm text-[#E5E5EA]/80">
                <p>
                  <span className="font-semibold">Expertise:</span>{' '}
                  I possess verifiable paid experience as a professional performer and have the relevant expertise in my artistic field.
                </p>
                <p>
                  <span className="font-semibold">Experience:</span>{' '}
                  I have a proven history of performing at significant events, such as major festivals, corporate galas, and international concerts.
                </p>
                <p>
                  <span className="font-semibold">Finance:</span>{' '}
                  The performance fee I have provided represents my standard professional rate.
                </p>
              </div>
              <div className="mt-3">
                <Checkbox
                  checked={eligibilityConsentState.agreed}
                  onCheckedChange={handleEligibilityConsentChange}
                  label="I certify that the information provided above is true and correct. I understand that any inaccurate statements may result in the immediate revocation of my elite eligibility."
                  className="items-start gap-3 [&_label]:text-xs [&_label]:text-[#E5E5EA] [&_label]:font-normal border-t border-[#3A3A3C]/80 pt-3"
                />
              </div>
              <div className="mt-5 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  className="px-4 py-2 text-sm rounded-xl border border-[#3A3A3C] bg-transparent text-[#E5E5EA] hover:bg-[#2C2C2E]"
                  onClick={closeEligibilityModal}
                  disabled={eligibilityConsentState.submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="active"
                  className="px-4 py-2 text-sm font-semibold rounded-xl bg-[#F55C8D] hover:bg-[#F33363] active:bg-[#D92D58] disabled:opacity-70"
                  onClick={handleSubmitEligibility}
                  disabled={
                    eligibilityConsentState.submitting ||
                    !eligibilityConsentState.agreed
                  }
                >
                  {eligibilityConsentState.submitting ? (
                    <span className="flex items-center gap-2">
                      <Loader size="small" />
                      Submitting...
                    </span>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
